// React Query hooks over the bare REST client. Components shouldn't touch
// fetch / setState for server data — they ask for hooks here.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import {
  coll,
  subscribe,
  type Subscription,
} from "./ddb";
import type {
  ChangeEvent,
  Condition,
  Id,
  Project,
  QueryOptions,
} from "./types";
import { allCabinets } from "./cabinets/projectOps";

// ── Generic CRUD hooks ────────────────────────────────────────────

export function useCollection<T>(
  collection: string,
  conds: Condition[] = [],
  opts: QueryOptions = {},
) {
  return useQuery({
    queryKey: ["coll", collection, conds, opts],
    queryFn: () => coll(collection).query<T>(conds, opts),
    staleTime: 5_000,
  });
}

export function useObject<T>(collection: string, id: Id | null | undefined) {
  return useQuery({
    queryKey: ["obj", collection, id],
    queryFn: () => coll(collection).find<T>(id as Id),
    enabled: id != null,
    staleTime: 5_000,
  });
}

export function useCreate<T>(collection: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: Partial<T>) => coll(collection).create<T>(fields),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["coll", collection] }); },
  });
}

export function useUpdate<T>(collection: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, changes }: { id: Id; changes: Partial<T> }) =>
      coll(collection).update<T>(id, changes),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["obj", collection, vars.id] });
      void qc.invalidateQueries({ queryKey: ["coll", collection] });
    },
  });
}

export function useRemove(collection: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: Id) => coll(collection).remove(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["coll", collection] }); },
  });
}

// ── Subscription hook (WS-backed) ──────────────────────────────────

export function useSubscription<T extends { _id: Id }>(
  collection: string,
  conds: Condition[],
  onChange: (e: ChangeEvent<T>) => void,
  enabled = true,
) {
  const subRef = useRef<Subscription | null>(null);
  const cbRef = useRef(onChange);
  cbRef.current = onChange;

  useEffect(() => {
    if (!enabled) return;
    const sub = subscribe<T>(collection, conds, (e) => cbRef.current(e));
    subRef.current = sub;
    return () => {
      sub.close();
      subRef.current = null;
    };
    // We deliberately stringify conds to track shape changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collection, JSON.stringify(conds), enabled]);
}

// ── Domain-typed sugar ────────────────────────────────────────────

export const useProjects   = (opts?: QueryOptions) => useCollection<Project>("projects", [], opts ?? { sort: "-updatedAt" });
export const useProject    = (id: Id | null | undefined) => useObject<Project>("projects", id);

/** Flat list of every cabinet inside a project. Derived from `useProject`
 *  — there's no separate `cabinets` collection anymore. Returns a stable
 *  result shape (`{ data, isLoading, error }`) so consumer code that
 *  previously called the cross-collection version doesn't need to branch
 *  on shape. */
export function useCabinetsIn(projectId: Id | null | undefined) {
  const q = useProject(projectId);
  return {
    ...q,
    data: q.data ? allCabinets(q.data) : undefined,
  };
}

/** Garniturs in a project — read straight off `project.garniturs`. */
export function useGarniturs(projectId: Id | null | undefined) {
  const q = useProject(projectId);
  return {
    ...q,
    data: q.data?.garniturs ?? undefined,
  };
}
